import React, { useState } from 'react';
import { MoreHorizontal, Trash2, Copy, FolderInput, ListTree } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { useAuth } from '../../context/AuthContext';
import { fmtMoney } from '../../lib/format';

const NumCell = ({ value, onCommit, disabled, testid, width = 'w-20', step = '0.01' }) => {
  const [val, setVal] = useState(value);
  React.useEffect(() => setVal(value), [value]);
  return (
    <Input
      type="number"
      step={step}
      value={val ?? ''}
      disabled={disabled}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { if (String(val) !== String(value)) onCommit(Number(val)); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      className={`h-8 ${width} text-right font-mono text-[12.5px] px-2`}
      data-testid={testid}
    />
  );
};

/**
 * Zona central: tabla de conceptos agrupados con celdas editables.
 */
export const ConceptsTable = ({ quotation, frozen, selectedConceptId, onOpenApu, editor }) => {
  const { canDelete } = useAuth();
  const groups = quotation?.groups || [];
  const moneda = quotation?.moneda || 'MXN';

  const commitConcept = (conceptId, field) => (v) => editor.updateConcept.mutate({ conceptId, patch: { [field]: v } });

  return (
    <div className="h-full overflow-auto scrollbar-thin" data-testid="apu-concepts-table">
      <Table>
        <TableHeader className="sticky top-0 bg-card z-10">
          <TableRow className="hover:bg-transparent">
            <TableHead className="th-label w-16">No.</TableHead>
            <TableHead className="th-label min-w-[220px]">Descripción</TableHead>
            <TableHead className="th-label w-16">Unidad</TableHead>
            <TableHead className="th-label w-24">Cantidad</TableHead>
            <TableHead className="th-label w-20">% Ind.</TableHead>
            <TableHead className="th-label w-20">% Fin.</TableHead>
            <TableHead className="th-label w-20">% Util.</TableHead>
            <TableHead className="th-label text-right w-28">C. Directo</TableHead>
            <TableHead className="th-label text-right w-28">P. Unitario</TableHead>
            <TableHead className="th-label text-right w-32">Total</TableHead>
            <TableHead className="th-label w-12"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.length === 0 && (
            <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-12">Crea un grupo en el panel izquierdo para comenzar</TableCell></TableRow>
          )}
          {groups.map((g) => (
            <React.Fragment key={g.id}>
              <TableRow className="bg-background/50 hover:bg-background/50">
                <TableCell colSpan={9} className="font-semibold text-[13px] py-2">{g.nombre_grupo}</TableCell>
                <TableCell className="money font-semibold text-[13px]">
                  {fmtMoney((g.concepts || []).reduce((s, c) => s + (c.calculated_total || 0), 0), moneda)}
                </TableCell>
                <TableCell />
              </TableRow>
              {(g.concepts || []).map((c) => (
                <TableRow
                  key={c.id}
                  className={`hover:bg-background/40 ${selectedConceptId === c.id ? 'bg-primary/5' : ''}`}
                  data-testid={`concept-row-${c.id}`}
                >
                  <TableCell className="font-mono text-[12.5px] text-muted-foreground">{c.numero_concepto}</TableCell>
                  <TableCell>
                    <button
                      onClick={() => onOpenApu(c)}
                      className="text-left text-[13px] hover:text-primary transition-colors duration-150 underline-offset-2 hover:underline"
                      data-testid={`open-apu-${c.id}`}
                      title="Abrir APU del concepto"
                    >
                      {c.descripcion}
                    </button>
                  </TableCell>
                  <TableCell className="text-[12.5px] text-muted-foreground">{c.unidad}</TableCell>
                  <TableCell>
                    <NumCell value={c.cantidad} disabled={frozen} testid={`concept-cantidad-${c.id}`} onCommit={commitConcept(c.id, 'cantidad')} />
                  </TableCell>
                  <TableCell>
                    <NumCell value={c.indirect_percentage} disabled={frozen} width="w-16" testid={`concept-ind-${c.id}`} onCommit={commitConcept(c.id, 'indirect_percentage')} />
                  </TableCell>
                  <TableCell>
                    <NumCell value={c.financing_percentage} disabled={frozen} width="w-16" testid={`concept-fin-${c.id}`} onCommit={commitConcept(c.id, 'financing_percentage')} />
                  </TableCell>
                  <TableCell>
                    <NumCell value={c.utility_percentage} disabled={frozen} width="w-16" testid={`concept-uti-${c.id}`} onCommit={commitConcept(c.id, 'utility_percentage')} />
                  </TableCell>
                  <TableCell className="money text-[12.5px]">{fmtMoney(c.direct_cost, moneda)}</TableCell>
                  <TableCell className="money text-[12.5px]">{fmtMoney(c.calculated_unit_price, moneda)}</TableCell>
                  <TableCell className="money text-[13px] font-medium">{fmtMoney(c.calculated_total, moneda)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`concept-menu-${c.id}`}>
                          <MoreHorizontal size={14} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onOpenApu(c)} data-testid="concept-action-apu">
                          <ListTree size={14} className="mr-2" /> Abrir APU
                        </DropdownMenuItem>
                        {!frozen && (
                          <>
                            <DropdownMenuItem onClick={() => editor.duplicateConcept.mutate(c.id)} data-testid="concept-action-duplicate">
                              <Copy size={14} className="mr-2" /> Duplicar
                            </DropdownMenuItem>
                            {groups.length > 1 && (
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger data-testid="concept-action-move">
                                  <FolderInput size={14} className="mr-2" /> Mover a grupo
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent>
                                  {groups.filter((tg) => tg.id !== g.id).map((tg) => (
                                    <DropdownMenuItem key={tg.id} onClick={() => editor.moveConcept.mutate({ conceptId: c.id, targetGroupId: tg.id })}>
                                      {tg.nombre_grupo}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                            )}
                            {canDelete && (
                              <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => editor.deleteConcept.mutate(c.id)} data-testid="concept-action-delete">
                                <Trash2 size={14} className="mr-2" /> Eliminar
                              </DropdownMenuItem>
                            )}
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
